import { useCallback, useState } from 'react';

import OrderSerialMaster from '../../../../Models/EntityModels/OrderSerialMaster';
import { MAX_LENGTH } from '../Gen1ApplicationValidation';
import {
    buildSearchSuccessRepeaterPatch,
    syncOrderSearchResultSelection,
} from '../repeater/gen1ApplicationRepeaterMerge';
import {
    buildOrderSearchConfirmMessage,
    ORDER_SEARCH_API_ERROR_MESSAGE,
    ORDER_SEARCH_NO_RESULTS_MESSAGE,
} from '../state/gen1ApplicationTrunkingReset';
import { hasSearchAddRepeaterLines } from '../gen1RepeaterLineHelpers';

export function getInitialOrderSearchState() {
    return {
        orderSearchResult: [],
        orderSearchLoading: false,
        orderSearchError: '',
        orderSearchModalOpen: false,
    };
}

/**
 * SO# Search の UI state と handlers。gen1Order は callback 経由で orchestrator が更新する。
 *
 * @param {Object} params
 * @param {string} params.orderNumber
 * @param {Object[]} params.repeaterSerials
 * @param {import('react').RefObject<HTMLElement>} params.orderNumberSearchSectionRef
 * @param {number} [params.editOrderId]
 * @param {(csrftoken: string, orderNumber: string, excludeHeaderId?: number) => Promise<Object[]>} params.fetchOrderSerialsByOrderNumber
 * @param {string} params.csrftoken
 * @param {(message: string) => void} params.onOrderNumberValidationError
 * @param {() => void} params.onClearOrderNumberValidationError
 * @param {(patch: Object) => void} params.onSearchSuccessRepeater
 * @param {(orderNumber: string, items: Object[]) => void} params.onRegisterSearchRepeater
 * @param {(lineSequenceNumber: number) => { deletedLine: Object|null, nextState: Object }|null} params.onDeleteSearchRepeaterLine
 */
export function useGen1OrderSerialSearch({
    orderNumber,
    editOrderId = 0,
    repeaterSerials,
    orderNumberSearchSectionRef,
    fetchOrderSerialsByOrderNumber,
    csrftoken,
    onOrderNumberValidationError,
    onClearOrderNumberValidationError,
    onSearchSuccessRepeater,
    onRegisterSearchRepeater,
    onDeleteSearchRepeaterLine,
}) {
    const [orderSearchResult, setOrderSearchResult] = useState(
        () => getInitialOrderSearchState().orderSearchResult
    );
    const [orderSearchLoading, setOrderSearchLoading] = useState(false);
    const [orderSearchError, setOrderSearchError] = useState('');
    const [orderSearchModalOpen, setOrderSearchModalOpen] = useState(false);

    const resetOrderSearchState = useCallback(() => {
        const initial = getInitialOrderSearchState();
        setOrderSearchResult(initial.orderSearchResult);
        setOrderSearchLoading(initial.orderSearchLoading);
        setOrderSearchError(initial.orderSearchError);
        setOrderSearchModalOpen(initial.orderSearchModalOpen);
    }, []);

    const scrollOrderNumberSearchIntoView = useCallback(() => {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                orderNumberSearchSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            });
        });
    }, [orderNumberSearchSectionRef]);

    const onSearchClick = useCallback(() => {
        const trimmedOrderNumber = (orderNumber ?? '').trim();
        if (!trimmedOrderNumber) {
            onOrderNumberValidationError(
                'Sales Order Number (SO#) is required.'
            );
            scrollOrderNumberSearchIntoView();
            return;
        }
        if (trimmedOrderNumber.length > MAX_LENGTH) {
            onOrderNumberValidationError(
                `Please enter the Sales Order Number (SO#) within ${MAX_LENGTH} characters.`
            );
            scrollOrderNumberSearchIntoView();
            return;
        }

        if (hasSearchAddRepeaterLines(repeaterSerials)) {
            const ok = window.confirm(
                buildOrderSearchConfirmMessage(trimmedOrderNumber)
            );
            if (!ok) {
                return;
            }
        }

        setOrderSearchLoading(true);
        setOrderSearchError('');
        onClearOrderNumberValidationError();

        // editOrderId は Search API のみに渡す。既存注文（Ordered 等）編集時、自注文の ADD 済み Serial が横断履歴で「使用中」と判定され候補から消えるのを防ぐ（excludeHeaderId）。
        fetchOrderSerialsByOrderNumber(
            csrftoken,
            trimmedOrderNumber,
            Number(editOrderId) || 0
        )
            .then((result) => {
                const rows = (result ?? []).map(
                    (item) => new OrderSerialMaster(item)
                );
                const patch = buildSearchSuccessRepeaterPatch({
                    prevLines: repeaterSerials,
                    orderNumber: trimmedOrderNumber,
                    fetchedRows: rows,
                });
                onSearchSuccessRepeater(patch);
                setOrderSearchLoading(false);
                setOrderSearchResult(patch.orderSearchResult);
                setOrderSearchModalOpen(patch.hasResults);
                setOrderSearchError(
                    patch.hasResults ? '' : ORDER_SEARCH_NO_RESULTS_MESSAGE
                );
            })
            .catch((e) => {
                setOrderSearchLoading(false);
                setOrderSearchError(
                    e?.message ?? ORDER_SEARCH_API_ERROR_MESSAGE
                );
            });
    }, [
        orderNumber,
        repeaterSerials,
        onOrderNumberValidationError,
        onClearOrderNumberValidationError,
        onSearchSuccessRepeater,
        scrollOrderNumberSearchIntoView,
        fetchOrderSerialsByOrderNumber,
        csrftoken,
        editOrderId,
    ]);

    const onOrderSearchShowModalClick = useCallback(() => {
        if (orderSearchResult.length === 0) return;

        const trimmedOrderNumber = String(orderNumber ?? '').trim();
        setOrderSearchModalOpen(true);
        setOrderSearchResult((result) =>
            syncOrderSearchResultSelection(
                result,
                repeaterSerials,
                trimmedOrderNumber
            )
        );
    }, [orderNumber, orderSearchResult.length, repeaterSerials]);

    const onOrderSearchModalClose = useCallback(() => {
        setOrderSearchModalOpen(false);
    }, []);

    const onOrderSearchSelectAllChange = useCallback((evn, data) => {
        const checked = data.checked;
        setOrderSearchResult((prev) =>
            prev.map((r) => ({ ...r, selected: checked }))
        );
    }, []);

    const onOrderSearchRowToggle = useCallback((idx) => {
        setOrderSearchResult((prev) =>
            prev.map((r, i) =>
                i === idx ? { ...r, selected: !r.selected } : r
            )
        );
    }, []);

    const onOrderSearchRegisterClick = useCallback(() => {
        const items = orderSearchResult.filter((r) => r.selected);
        const trimmedOrderNumber = String(orderNumber ?? '').trim();
        onRegisterSearchRepeater(trimmedOrderNumber, items);
        setOrderSearchModalOpen(false);
    }, [orderNumber, orderSearchResult, onRegisterSearchRepeater]);

    const onDeleteSearchRepeaterRow = useCallback(
        (lineSequenceNumber) => {
            const deleteResult = onDeleteSearchRepeaterLine(lineSequenceNumber);
            if (deleteResult?.deletedLine == null) {
                return;
            }
            const trimmedOrderNumber = String(orderNumber ?? '').trim();
            const nextSerials =
                deleteResult.nextState?.gen1Order
                    ?.Gen1TrunkingOrderRepeaterSerials ?? [];
            setOrderSearchResult((prev) =>
                syncOrderSearchResultSelection(
                    prev,
                    nextSerials,
                    trimmedOrderNumber
                )
            );
        },
        [onDeleteSearchRepeaterLine, orderNumber]
    );

    return {
        orderSearchResult,
        orderSearchLoading,
        orderSearchError,
        orderSearchModalOpen,
        onSearchClick,
        onOrderSearchShowModalClick,
        onOrderSearchModalClose,
        onOrderSearchSelectAllChange,
        onOrderSearchRowToggle,
        onOrderSearchRegisterClick,
        onDeleteSearchRepeaterRow,
        resetOrderSearchState,
    };
}
